import { useQuery } from '@tanstack/react-query'
import type Stripe from 'stripe'
import useAuthUser from '~/src/hooks/useAuthUser'
import fetcher from '~/src/lib/fetcher'
import logger from '~/src/lib/logger'

export interface ApplePayLineItem {
  label: string
  amount: number
}

// Works on frontend and backend. NEXT_PUBLIC_VERCEL_ENV is set on frontend, VERCEL_ENV is set on backend.
const ENV_SET =
  process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.VERCEL_ENV || ''

export const STRIPE_PUBLISHABLE_KEY =
  ENV_SET === 'production'
    ? process.env.NEXT_PUBLIC_STRIPE_LIVE_PUBLISHABLE_KEY
    : process.env.NEXT_PUBLIC_STRIPE_TEST_PUBLISHABLE_KEY

export type ApplePayRegularBilling = ApplePayLineItem & {
  recurringPaymentStartDate?: Date
  recurringPaymentEndDate?: Date
  recurringPaymentIntervalUnit?: 'year' | 'month' | 'day' | 'hour' | 'minute'
  recurringPaymentIntervalCount?: number
}

export interface StripeValidationResponse {
  success: boolean
  customerId?: string
  subscriptionId?: string
  status?: Stripe.Checkout.Session.Status | null
  paymentStatus?: string
  liveMode?: boolean
  error?: any
  session?: Stripe.Checkout.Session
  sessionId?: string
}

export interface StripeCustomerResponse {
  success: boolean
  customer?: Stripe.Customer
  customerId?: string
  error?: any
}

export async function validateStripeSession(
  sessionId: string,
  authToken: string
): Promise<StripeValidationResponse> {
  try {
    const resp = await fetcher<{
      success: boolean
      session?: Stripe.Checkout.Session
      sessionId?: string
      error?: any
    }>('/api/stripe/validate-session', {
      method: 'POST',
      dataOrParams: {
        session_id: sessionId,
      },
      axiosConfig: {
        headers: {
          'x-authorization': authToken,
        },
      },
    })

    const { success, session: stripeSession, error } = resp

    if (!success) {
      return {
        success: false,
        error: error || 'Error validating stripe session',
      }
    }

    return {
      success: true,
      customerId: stripeSession?.customer as string,
      subscriptionId: stripeSession?.subscription as string,
      status: stripeSession?.status,
      paymentStatus: stripeSession?.payment_status,
      liveMode: stripeSession?.livemode === true,
      session: stripeSession,
      sessionId,
    }
  } catch (error) {
    logger.error('Error validating stripe session:', { error })
    return {
      success: false,
      error: 'Error validating stripe session',
    }
  }
}

export function useGetStripeCustomer(customerId?: string) {
  const { authUser } = useAuthUser()

  return useQuery({
    queryKey: ['stripe-customer', customerId],
    queryFn: async () => {
      if (!customerId || !authUser) {
        throw new Error('Customer ID and auth user are required')
      }

      try {
        const resp = await fetcher<{
          success: boolean
          customer?: Stripe.Customer
          error?: any
        }>('/api/stripe/get-customer', {
          method: 'POST',
          dataOrParams: {
            customer_id: customerId,
          },
          authUser,
        })

        const { success, customer, error } = resp

        if (!success) {
          return {
            success: false,
            error: error || 'Error retrieving stripe customer',
            customerId,
          }
        }

        return {
          success: true,
          customer,
          customerId,
        }
      } catch (error) {
        logger.error('Error retrieving stripe customer:', { error })
        return {
          success: false,
          error: 'Error retrieving stripe customer',
          customerId,
        }
      }
    },
    enabled: !!customerId && !!authUser,
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 60, // Keep unused data in cache for 1 hour
  })
}
